import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authErrorMessage, passwordMeetsPolicy, signUp } from '../auth/cognito';
import {
  AuthLayout,
  FormAlert,
  PasswordChecklist,
  describedBy,
  type AuthRouteState,
} from '../components/AuthLayout';

export function SignupPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);

  // The checklist is the live feedback for the policy, so the inline error only
  // appears once a submit has actually been turned away by it.
  const weakPassword = attempted && !passwordMeetsPolicy(password);
  const mismatch = confirm !== '' && confirm !== password;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    setAttempted(true);
    setError(null);

    if (!passwordMeetsPolicy(password)) return;
    if (password !== confirm) return;

    const address = email.trim();
    setBusy(true);
    try {
      await signUp(address, password);
      const state: AuthRouteState = {
        email: address,
        notice: 'We sent a six-digit code to your email. Enter it to finish setting up your account.',
      };
      // Replace: the back button should not return to a signup form whose
      // email is now taken by the account it just created.
      navigate('/verify', { state, replace: true });
    } catch (caught: unknown) {
      setError(authErrorMessage(caught, 'signUp'));
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Your portfolio page takes a few minutes to set up."
      footer={
        <>
          Already have an account? <Link className="link" to="/login">Sign in</Link>
        </>
      }
    >
      <form className="stack" onSubmit={onSubmit} aria-busy={busy}>
        <fieldset className="fieldset" disabled={busy}>
          {error === null ? null : (
            <FormAlert kind="error" id="signup-error">
              {error}
            </FormAlert>
          )}

          <div className="field">
            <label className="label" htmlFor="signup-email">
              Email
            </label>
            <input
              id="signup-email"
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={describedBy('signup-email-hint', error !== null && 'signup-error')}
            />
            <p className="hint" id="signup-email-hint">
              This is where your confirmation code and any brand enquiries go.
            </p>
          </div>

          <div className="field">
            <label className="label" htmlFor="signup-password">
              Password
            </label>
            <input
              id="signup-password"
              className="input"
              type="password"
              name="new-password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={weakPassword ? true : undefined}
              aria-describedby={describedBy(
                'signup-password-rules',
                weakPassword && 'signup-password-error',
              )}
            />
            <PasswordChecklist id="signup-password-rules" value={password} />
            {weakPassword ? (
              <p className="error-text" id="signup-password-error">
                Your password still needs everything ticked above.
              </p>
            ) : null}
          </div>

          <div className="field">
            <label className="label" htmlFor="signup-confirm">
              Confirm password
            </label>
            <input
              id="signup-confirm"
              className="input"
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              aria-invalid={mismatch ? true : undefined}
              aria-describedby={describedBy(mismatch && 'signup-confirm-error')}
            />
            {mismatch ? (
              <p className="error-text" id="signup-confirm-error">
                Those two passwords are not the same.
              </p>
            ) : null}
          </div>

          <button type="submit" className="btn btn--primary btn--block">
            {busy ? <span className="spinner spinner--sm" aria-hidden="true" /> : null}
            {busy ? 'Creating your account…' : 'Create account'}
          </button>
        </fieldset>
      </form>
    </AuthLayout>
  );
}
